import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Plus, FileText, DollarSign, CheckCircle, Clock, 
  Eye, Trash2, MoreVertical, Upload, Receipt, Building2,
  Calendar, AlertCircle, CheckCircle2, Edit2, X, Paperclip,
  Percent, Calculator, Send, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import BlueprintIndicator from '@/components/Blueprint/BlueprintIndicator';
import HelpIcon from '@/components/HelpIcon';
import { DragDropUpload } from '@/components/ui/DragDropUpload';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';
import { markupPair } from '@/utils/markup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: X },
  included_in_draw: { label: 'In Draw Request', color: 'bg-purple-100 text-purple-700', icon: Receipt },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
};

const CATEGORY_OPTIONS = [
  { value: 'labor', label: 'Labor' },
  { value: 'materials', label: 'Materials' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'permits', label: 'Permits & Fees' },
  { value: 'other', label: 'Other' },
];

export default function ReceivedInvoicesPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [receivedInvoices, setReceivedInvoices] = useState([]);
  const [summary, setSummary] = useState({});
  const [markupSettings, setMarkupSettings] = useState({ default_markup_percent: 0, markup_label: 'Project Consulting & QC Management' });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Purchase Orders for matching
  const [availablePOs, setAvailablePOs] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showMarkupDialog, setShowMarkupDialog] = useState(false);
  const [showCreateYourInvoiceDialog, setShowCreateYourInvoiceDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // New received invoice form
  const [newInvoice, setNewInvoice] = useState({
    vendor_name: '',
    vendor_contact: '',
    vendor_email: '',
    vendor_phone: '',
    vendor_address: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    received_date: new Date().toISOString().split('T')[0],
    due_date: '',
    description: '',
    category: 'labor',
    amount: '',
    purchase_order_id: '',
    document_url: '',
    document_name: ''
  });
  
  // Create your invoice form
  const [yourInvoiceData, setYourInvoiceData] = useState({
    markup_percent: 0,
    markup_label: 'Project Consulting & QC Management'
  });
  
  const darkMode = document.documentElement.classList.contains('dark');
  
  useEffect(() => {
    fetchData();
  }, [projectId, statusFilter]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectRes, invoicesRes, markupRes, posRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/received-invoices`, {
          params: statusFilter !== 'all' ? { status: statusFilter } : {}
        }),
        axios.get(`${API}/projects/${projectId}/markup-settings`).catch(() => ({ data: { default_markup_percent: 0, markup_label: 'Project Consulting & QC Management' } })),
        axios.get(`${API}/projects/${projectId}/purchase-orders-for-matching`).catch(() => ({ data: { purchase_orders: [] } }))
      ]);
      
      setProject(projectRes.data);
      setReceivedInvoices(invoicesRes.data.received_invoices || []);
      setSummary(invoicesRes.data.summary || {});
      setMarkupSettings(markupRes.data);
      setAvailablePOs(posRes.data.purchase_orders || []);
      setYourInvoiceData({
        markup_percent: markupRes.data.default_markup_percent || 0,
        markup_label: markupRes.data.markup_label || 'Project Consulting & QC Management'
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load received invoices');
    } finally {
      setLoading(false);
    }
  };
  
  const createReceivedInvoice = async () => {
    try {
      if (!newInvoice.vendor_name || !newInvoice.amount) {
        toast.error('Vendor name and amount are required');
        return;
      }
      
      await axios.post(`${API}/projects/${projectId}/received-invoices`, newInvoice);
      toast.success('Received invoice added');
      setShowCreateDialog(false);
      resetNewInvoiceForm();
      fetchData();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Failed to add received invoice');
    }
  };
  
  const resetNewInvoiceForm = () => {
    setNewInvoice({
      vendor_name: '',
      vendor_contact: '',
      vendor_email: '',
      vendor_phone: '',
      vendor_address: '',
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      received_date: new Date().toISOString().split('T')[0],
      due_date: '',
      description: '',
      category: 'labor',
      amount: '',
      purchase_order_id: '',
      document_url: '',
      document_name: ''
    });
    setSelectedPO(null);
  };
  
  const handlePOSelection = (poId) => {
    if (!poId || poId === 'none') {
      setSelectedPO(null);
      setNewInvoice(prev => ({
        ...prev,
        purchase_order_id: '',
        vendor_name: '',
        description: ''
      }));
      return;
    }
    
    const po = availablePOs.find(p => p.id === poId);
    if (po) {
      setSelectedPO(po);
      setNewInvoice(prev => ({
        ...prev,
        purchase_order_id: poId,
        vendor_name: po.vendor_name || '',
        description: `Invoice for ${po.po_number}`,
        // Suggest the remaining amount as default
        amount: po.remaining_to_invoice > 0 ? po.remaining_to_invoice.toFixed(2) : ''
      }));
    }
  };
  
  const handleDocumentUpload = async (file) => {
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'received-invoices');
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const fileUrl = response.data.url || response.data.file_url;
      setNewInvoice(prev => ({
        ...prev,
        document_url: fileUrl,
        document_name: file.name
      }));
      toast.success('Document uploaded');
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    }
  };
  
  const approveInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/received-invoices/${invoiceId}/approve`, {
        reviewed_by: 'current_user'
      });
      toast.success('Invoice approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve invoice');
    }
  };
  
  const deleteInvoice = async (invoiceId) => {
    try {
      await axios.delete(`${API}/received-invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };
  
  const updateMarkupSettings = async () => {
    try {
      await axios.put(`${API}/projects/${projectId}/markup-settings`, {
        default_markup_percent: parseFloat(markupSettings.default_markup_percent) || 0,
        markup_label: markupSettings.markup_label
      });
      toast.success('Markup settings saved');
      setShowMarkupDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save markup settings');
    }
  };
  
  const createYourInvoice = async () => {
    try {
      if (!selectedInvoice) return;
      
      const response = await axios.post(`${API}/received-invoices/${selectedInvoice.id}/create-your-invoice`, {
        markup_percent: parseFloat(yourInvoiceData.markup_percent) || 0,
        markup_label: yourInvoiceData.markup_label
      });
      
      toast.success('Your invoice created successfully');
      setShowCreateYourInvoiceDialog(false);
      setSelectedInvoice(null);
      fetchData();
      
      // Show the breakdown
      const breakdown = response.data.breakdown;
      toast.info(`Invoice Total: ${formatCurrency(breakdown.total)} (${formatCurrency(breakdown.contractor_amount)} + ${yourInvoiceData.markup_percent}% markup)`);
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Failed to create invoice');
    }
  };
  
  const addToDrawRequest = (invoice) => {
    // Navigate to Draw Requests page with the invoice pre-selected
    navigate(`/projects/${projectId}/draw-requests`, { 
      state: { preSelectedInvoice: invoice } 
    });
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#BC5D41]"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D] text-[#E5E2DD]' : 'bg-[#FAF9F7]'}`}>
      {/* Header */}
      <div className={`border-b ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'} sticky top-0 z-10`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate(`/projects/${projectId}/hub`)}
                className={darkMode ? 'text-[#8A8A8A] hover:text-[#E5E2DD]' : ''}
                data-testid="back-to-project-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Project
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-semibold">Received Invoices</h1>
                  <HelpIcon elementId="received-invoices-page" position="inline" />
                  <BlueprintIndicator featureId="received_invoices" size="sm" />
                </div>
                <p className="text-sm text-muted-foreground">{project?.name} • Contractor & Vendor Invoices</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowMarkupDialog(true)}
                data-testid="markup-settings-btn"
              >
                <Percent className="w-4 h-4 mr-2" />
                Markup: {markupPair(markupSettings.default_markup_percent)}{markupSettings.markup_source === 'firm' ? ' · firm default' : ''}
              </Button>
              <Button 
                size="sm"
                className="bg-[#BC5D41] hover:bg-[#A34D35] text-white"
                onClick={() => setShowCreateDialog(true)}
                data-testid="add-received-invoice-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Received Invoice
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Receipt className="w-4 h-4" />
              Total Received
            </div>
            <p className="text-2xl font-semibold">{summary.total_count || 0}</p>
          </div>
          
          <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Clock className="w-4 h-4" />
              Pending Review
            </div>
            <p className="text-2xl font-semibold text-amber-600">{formatCurrency(summary.pending_review)}</p>
          </div>
          
          <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <CheckCircle className="w-4 h-4" />
              Approved
            </div>
            <p className="text-2xl font-semibold text-blue-600">{formatCurrency(summary.approved)}</p>
          </div>
          
          <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <DollarSign className="w-4 h-4" />
              In Draw Requests
            </div>
            <p className="text-2xl font-semibold text-purple-600">{formatCurrency(summary.included_in_draw)}</p>
          </div>
        </div>
        
        {/* Filter */}
        <div className="flex items-center gap-4 mb-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Invoices</SelectItem>
              <SelectItem value="pending_review">Pending Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="included_in_draw">In Draw Request</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Invoices List */}
        <div className={`rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
          <div className="p-4 border-b border-inherit">
            <h2 className="font-medium">Received Invoices from Contractors & Vendors</h2>
          </div>
          
          {receivedInvoices.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Receipt className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No received invoices yet.</p>
              <p className="text-sm">Add invoices from contractors and vendors to track and process.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Linked PO</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Variance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {receivedInvoices.map((invoice) => {
                  const StatusIcon = STATUS_CONFIG[invoice.status]?.icon || Clock;
                  const hasVariance = invoice.po_variance && Math.abs(invoice.po_variance) > 0.01;
                  return (
                    <TableRow key={invoice.id} data-testid={`received-invoice-row-${invoice.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{invoice.vendor_name}</p>
                          {invoice.vendor_contact && (
                            <p className="text-xs text-muted-foreground">{invoice.vendor_contact}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{invoice.invoice_number || '-'}</TableCell>
                      <TableCell>
                        {invoice.linked_po ? (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            <FileText className="w-3 h-3 mr-1" />
                            {invoice.linked_po.po_number}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{invoice.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">
                          {invoice.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{invoice.invoice_date}</TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(invoice.amount)}</TableCell>
                      <TableCell>
                        {hasVariance ? (
                          <Badge className={invoice.po_variance > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {invoice.po_variance > 0 ? '+' : ''}{formatCurrency(invoice.po_variance)}
                          </Badge>
                        ) : invoice.linked_po ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Match
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_CONFIG[invoice.status]?.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {STATUS_CONFIG[invoice.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`invoice-actions-${invoice.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {invoice.document_url && (
                              <DropdownMenuItem onClick={() => window.open(invoice.document_url, '_blank')}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Document
                              </DropdownMenuItem>
                            )}
                            
                            {invoice.status === 'pending_review' && (
                              <>
                                <DropdownMenuItem onClick={() => approveInvoice(invoice.id)}>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Approve
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            
                            {(invoice.status === 'approved' || invoice.status === 'pending_review') && !invoice.your_invoice_id && (
                              <DropdownMenuItem onClick={() => {
                                setSelectedInvoice(invoice);
                                setYourInvoiceData({
                                  markup_percent: markupSettings.default_markup_percent,
                                  markup_label: markupSettings.markup_label
                                });
                                setShowCreateYourInvoiceDialog(true);
                              }}>
                                <Calculator className="w-4 h-4 mr-2" />
                                Create Your Invoice (+ Markup)
                              </DropdownMenuItem>
                            )}
                            
                            {invoice.your_invoice_id && (
                              <DropdownMenuItem onClick={() => navigate(`/financials`)}>
                                <FileText className="w-4 h-4 mr-2" />
                                View Your Invoice
                              </DropdownMenuItem>
                            )}
                            
                            <DropdownMenuItem onClick={() => addToDrawRequest(invoice)}>
                              <Receipt className="w-4 h-4 mr-2" />
                              Add to Draw Request
                            </DropdownMenuItem>
                            
                            <DropdownMenuSeparator />
                            
                            {invoice.status === 'pending_review' && (
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => deleteInvoice(invoice.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
      
      {/* Create Received Invoice Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Received Invoice</DialogTitle>
            <DialogDescription>
              Enter the invoice received from a contractor or vendor
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* PO Matching Section */}
            {availablePOs.length > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <Label className="text-blue-800 dark:text-blue-200 font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Link to Purchase Order (Optional)
                </Label>
                <Select
                  value={newInvoice.purchase_order_id || 'none'}
                  onValueChange={handlePOSelection}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select a PO to match..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No PO - Enter manually</SelectItem>
                    {availablePOs.map(po => (
                      <SelectItem key={po.id} value={po.id}>
                        <div className="flex items-center justify-between w-full">
                          <span className="font-medium">{po.po_number}</span>
                          <span className="text-muted-foreground ml-2">
                            {po.vendor_name} • ${po.total?.toLocaleString()}
                            {po.remaining_to_invoice > 0 && (
                              <span className="text-green-600 ml-1">
                                (${po.remaining_to_invoice.toLocaleString()} remaining)
                              </span>
                            )}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedPO && (
                  <div className={`mt-3 p-3 rounded border text-sm ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white'}`}>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">PO Total:</span>
                      <span>${selectedPO.total?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center text-muted-foreground">
                      <span>Already Invoiced:</span>
                      <span>${selectedPO.total_invoiced?.toLocaleString() || 0}</span>
                    </div>
                    <div className="flex justify-between items-center text-green-600 font-medium border-t pt-2 mt-2">
                      <span>Remaining:</span>
                      <span>${selectedPO.remaining_to_invoice?.toLocaleString() || selectedPO.total?.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Vendor Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vendor / Company Name *</Label>
                <Input 
                  value={newInvoice.vendor_name}
                  onChange={(e) => setNewInvoice({ ...newInvoice, vendor_name: e.target.value })}
                  placeholder="ABC Plumbing Co."
                />
              </div>
              <div>
                <Label>Contact Person</Label>
                <Input 
                  value={newInvoice.vendor_contact}
                  onChange={(e) => setNewInvoice({ ...newInvoice, vendor_contact: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={newInvoice.vendor_email}
                  onChange={(e) => setNewInvoice({ ...newInvoice, vendor_email: e.target.value })}
                  placeholder="vendor@email.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  value={newInvoice.vendor_phone}
                  onChange={(e) => setNewInvoice({ ...newInvoice, vendor_phone: e.target.value })}
                  placeholder="(555) 555-5555"
                />
              </div>
            </div>
            
            {/* Invoice Details */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Their Invoice #</Label>
                <Input 
                  value={newInvoice.invoice_number}
                  onChange={(e) => setNewInvoice({ ...newInvoice, invoice_number: e.target.value })}
                  placeholder="INV-12345"
                />
              </div>
              <div>
                <Label>Invoice Date</Label>
                <Input 
                  type="date"
                  value={newInvoice.invoice_date}
                  onChange={(e) => setNewInvoice({ ...newInvoice, invoice_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Received Date</Label>
                <Input 
                  type="date"
                  value={newInvoice.received_date}
                  onChange={(e) => setNewInvoice({ ...newInvoice, received_date: e.target.value })}
                />
              </div>
            </div>
            
            {/* Description & Amount */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label>Description of Work / Materials *</Label>
                <Textarea 
                  value={newInvoice.description}
                  onChange={(e) => setNewInvoice({ ...newInvoice, description: e.target.value })}
                  placeholder="Describe the work or materials..."
                  rows={2}
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select 
                  value={newInvoice.category} 
                  onValueChange={(v) => setNewInvoice({ ...newInvoice, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Invoice Amount *</Label>
                <Input 
                  type="number"
                  value={newInvoice.amount}
                  onChange={(e) => setNewInvoice({ ...newInvoice, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input 
                  type="date"
                  value={newInvoice.due_date}
                  onChange={(e) => setNewInvoice({ ...newInvoice, due_date: e.target.value })}
                />
              </div>
            </div>
            
            {/* Document Upload */}
            <div>
              <Label>Attach Invoice Document</Label>
              <DragDropUpload
                value={newInvoice.document_url}
                fileName={newInvoice.document_name}
                onUpload={(url, name, type) => setNewInvoice({ 
                  ...newInvoice, 
                  document_url: url, 
                  document_name: name 
                })}
                onRemove={() => setNewInvoice({ 
                  ...newInvoice, 
                  document_url: '', 
                  document_name: '' 
                })}
                folder="received-invoices"
                placeholder="Drag and drop or click to upload invoice"
                darkMode={darkMode}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button 
              className="bg-[#BC5D41] hover:bg-[#A34D35] text-white"
              onClick={createReceivedInvoice}
              disabled={!newInvoice.vendor_name || !newInvoice.amount}
              data-testid="create-received-invoice-submit"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Markup Settings Dialog */}
      <Dialog open={showMarkupDialog} onOpenChange={setShowMarkupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Project Markup Settings</DialogTitle>
            <DialogDescription>
              Set the default markup percentage for this project. This is applied when creating your invoices from contractor invoices.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Default Markup Percentage</Label>
              <div className="flex items-center gap-2">
                <Input 
                  type="number"
                  value={markupSettings.default_markup_percent}
                  onChange={(e) => setMarkupSettings({ ...markupSettings, default_markup_percent: e.target.value })}
                  placeholder="0"
                  className="w-24"
                />
                <span className="text-lg">%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                e.g., 15% markup on a $1,000 invoice = $150 fee
              </p>
            </div>
            
            <div>
              <Label>Markup Label (how it appears on invoices)</Label>
              <Input 
                value={markupSettings.markup_label}
                onChange={(e) => setMarkupSettings({ ...markupSettings, markup_label: e.target.value })}
                placeholder="Project Consulting & QC Management"
              />
            </div>
            
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-blue-50'}`}>
              <p className="text-sm">
                <strong>Example:</strong> With {markupSettings.default_markup_percent || 0}% markup, a $10,000 contractor invoice becomes a ${(10000 * (1 + (parseFloat(markupSettings.default_markup_percent) || 0) / 100)).toLocaleString()} client invoice.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarkupDialog(false)}>Cancel</Button>
            <Button 
              className="bg-[#BC5D41] hover:bg-[#A34D35] text-white"
              onClick={updateMarkupSettings}
            >
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Your Invoice Dialog */}
      <Dialog open={showCreateYourInvoiceDialog} onOpenChange={setShowCreateYourInvoiceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Your Invoice with Markup</DialogTitle>
            <DialogDescription>
              Create your invoice to the client based on this contractor invoice
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4">
              {/* Source Invoice Info */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-gray-50'}`}>
                <p className="text-sm font-medium mb-2">From: {selectedInvoice.vendor_name}</p>
                <p className="text-sm text-muted-foreground">{selectedInvoice.description}</p>
                <p className="text-lg font-semibold mt-2">{formatCurrency(selectedInvoice.amount)}</p>
              </div>
              
              {/* Markup Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Markup Percentage</Label>
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number"
                      value={yourInvoiceData.markup_percent}
                      onChange={(e) => setYourInvoiceData({ ...yourInvoiceData, markup_percent: e.target.value })}
                      placeholder="0"
                      className="w-24"
                    />
                    <span>%</span>
                  </div>
                </div>
                <div>
                  <Label>Your Fee Label</Label>
                  <Input 
                    value={yourInvoiceData.markup_label}
                    onChange={(e) => setYourInvoiceData({ ...yourInvoiceData, markup_label: e.target.value })}
                  />
                </div>
              </div>
              
              {/* Calculation Preview */}
              <div className={`p-4 rounded-lg border-2 ${darkMode ? 'border-[#2A2A2A] bg-[#0A0A0A]' : 'border-[#BC5D41]/20 bg-[#BC5D41]/5'}`}>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Contractor Amount:</span>
                    <span>{formatCurrency(selectedInvoice.amount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Your Fee ({yourInvoiceData.markup_percent}%):</span>
                    <span>{formatCurrency(selectedInvoice.amount * (parseFloat(yourInvoiceData.markup_percent) || 0) / 100)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-lg border-t pt-2 mt-2">
                    <span>Total to Client:</span>
                    <span className="text-[#BC5D41]">
                      {formatCurrency(selectedInvoice.amount * (1 + (parseFloat(yourInvoiceData.markup_percent) || 0) / 100))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateYourInvoiceDialog(false)}>Cancel</Button>
            <Button 
              className="bg-[#BC5D41] hover:bg-[#A34D35] text-white"
              onClick={createYourInvoice}
            >
              <Calculator className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
